// Points of interest (spec §4.6): a named place with an icon, an optional warning radius and details. One can be
// part of a route or stand alone in the Library. This app and XpeditionGO store them the same way (the shared POI
// format, docs/poi-format.md; examples in /shared/poi-format, which both apps' tests read):
// - a POI record is a point: pointType 2, name, description (the notes), warningRadius (m), the icon and alert
//   packed into the code as "xgo:<icon>:<alert>", and website and phone;
// - on a route: its records, as a JSON list, in the route's xgoPois extension (the legacy API keeps extensions but
//   strips POI details from route points). POI points among the route's points (older routes, GPX imports) are
//   read too, and moved into the extension when the route is saved;
// - on its own: a route of routeType 2 holding just that point (the API lists it as kind "poi"), with the website
//   and phone in the route's detail, as JSON (the API keeps no website or phone on a point).
import type { ApiExtension, ApiPoint, ApiTrail, LonLat } from './trail';
import { isPoi } from './trail';

export const POINT_OF_INTEREST = 2;

/** XpeditionGO's POI icons, in its order. */
export const POI_ICONS = [
  'pin',
  'viewpoint',
  'summit',
  'water',
  'camp',
  'shelter',
  'food',
  'cafe',
  'toilet',
  'parking',
  'danger',
  'gate',
  'bridge',
  'info',
  'photo',
] as const;
export type PoiIcon = (typeof POI_ICONS)[number];

/** How XpeditionGO tells the user they're inside the warning radius. */
export const POI_ALERTS = ['vibrate', 'alert', 'both'] as const;
export type PoiAlert = (typeof POI_ALERTS)[number];

export interface PoiDetails {
  name: string;
  notes: string;
  icon: PoiIcon;
  alert: PoiAlert;
  /** Metres; 0 for none. */
  warningRadiusM: number;
  website: string;
  phone: string;
}

export const emptyPoiDetails = (name = ''): PoiDetails => ({
  name,
  notes: '',
  icon: 'pin',
  alert: 'both',
  warningRadiusM: 0,
  website: '',
  phone: '',
});

/** The icon and alert from a point's code; anything else is a pin that alerts both ways (as XpeditionGO reads it). */
export function readPoiCode(code: string | undefined): { icon: PoiIcon; alert: PoiAlert } {
  const [prefix, icon, alert] = (code ?? '').split(':');
  return {
    icon: prefix === 'xgo' && POI_ICONS.includes(icon as PoiIcon) ? (icon as PoiIcon) : 'pin',
    alert: prefix === 'xgo' && POI_ALERTS.includes(alert as PoiAlert) ? (alert as PoiAlert) : 'both',
  };
}

/** The website and phone kept in a standalone POI's detail (JSON; anything else in there is left alone). */
function readDetail(detail: string | undefined): { website?: string; phone?: string } {
  if (!detail?.trim().startsWith('{')) return {};
  try {
    const value = JSON.parse(detail) as { website?: unknown; phone?: unknown };
    return {
      website: typeof value.website === 'string' ? value.website : undefined,
      phone: typeof value.phone === 'string' ? value.phone : undefined,
    };
  } catch {
    return {};
  }
}

export function poiDetailsOf(point: ApiPoint, detail?: string): PoiDetails {
  const fromDetail = readDetail(detail);
  const website = point.website ?? fromDetail.website ?? '';
  const phone = point.phone ?? fromDetail.phone ?? '';
  return {
    name: point.name ?? '',
    notes: point.description ?? '',
    ...readPoiCode(point.code),
    warningRadiusM: Math.max(0, Math.round(point.warningRadius ?? 0)),
    website,
    phone,
  };
}

/** The point for a POI at a place. */
export function poiPoint([longitude, latitude]: LonLat, details: PoiDetails, from?: ApiPoint): ApiPoint {
  return {
    ...from,
    latitude,
    longitude,
    pointType: POINT_OF_INTEREST,
    name: details.name.trim() || undefined,
    description: details.notes.trim() || undefined,
    code: `xgo:${details.icon}:${details.alert}`,
    warningRadius: details.warningRadiusM > 0 ? details.warningRadiusM : undefined,
    website: details.website.trim() || undefined,
    phone: details.phone.trim() || undefined,
  };
}

/** The route extension holding its points of interest (XpeditionGO's RemoteRouteDetail.poiExtension). */
export const POI_EXTENSION = 'xgoPois';

/** Whether an extension is the xgoPois one (the API may fill in its description or its attribute type). */
export const isPoiExtension = (ext: ApiExtension) =>
  ext.description === POI_EXTENSION ||
  ext.attributeType?.code === POI_EXTENSION ||
  ext.attributeType?.tag === POI_EXTENSION;

/** The POI records in a route's xgoPois extension; none if it is missing or unreadable. */
export function poisFromExtensions(extensions: ApiExtension[] | undefined): ApiPoint[] {
  const ext = extensions?.find(isPoiExtension);
  if (!ext?.valueString) return [];
  try {
    const list: unknown = JSON.parse(ext.valueString);
    if (!Array.isArray(list)) return [];
    return list.filter((value): value is ApiPoint => {
      const p = value as ApiPoint;
      return (
        !!p &&
        typeof p === 'object' &&
        Number.isFinite(p.latitude) &&
        Number.isFinite(p.longitude) &&
        isPoi(p)
      );
    });
  } catch {
    return [];
  }
}

const samePlace = (a: ApiPoint, b: ApiPoint) =>
  Math.abs(a.latitude - b.latitude) < 1e-6 && Math.abs(a.longitude - b.longitude) < 1e-6;

/**
 * A route's points of interest: the xgoPois extension's records, then any POI points among its points that aren't
 * already there (a record in the extension wins over a point at the same place).
 */
export function routePois(data: ApiTrail): ApiPoint[] {
  const records = poisFromExtensions(data.extensions);
  const points = (data.points ?? []).filter((p) => !p.deleted && isPoi(p));
  return [...records, ...points.filter((p) => !records.some((r) => samePlace(r, p)))];
}

/** A POI record as the extension keeps it (no ids: those belong to route points). */
const poiRecord = (p: ApiPoint): ApiPoint => ({
  latitude: p.latitude,
  longitude: p.longitude,
  ...(p.elevation !== undefined && p.elevation > -500 ? { elevation: p.elevation } : {}),
  pointType: POINT_OF_INTEREST,
  usage: 0,
  ...(p.name ? { name: p.name } : {}),
  ...(p.description ? { description: p.description } : {}),
  ...(p.code ? { code: p.code } : {}),
  ...(p.warningRadius ? { warningRadius: p.warningRadius } : {}),
  ...(p.website ? { website: p.website } : {}),
  ...(p.phone ? { phone: p.phone } : {}),
});

/** A route's extensions with its points of interest in the xgoPois one (no points of interest: no xgoPois). */
export function withPoiExtension(extensions: ApiExtension[] | undefined, pois: ApiPoint[]): ApiExtension[] {
  const kept = (extensions ?? []).filter((ext) => !isPoiExtension(ext));
  if (pois.length === 0) return kept;
  return [
    ...kept,
    {
      // The API keeps valueString only when the attribute type's dataType is String (1).
      attributeType: { code: POI_EXTENSION, tag: POI_EXTENSION, description: POI_EXTENSION, dataType: 1 },
      description: POI_EXTENSION,
      valueString: JSON.stringify(pois.map(poiRecord)),
    },
  ];
}

/** A standalone POI's JSON, new or with changed details (keeping its ids). */
export function poiJson(position: LonLat, details: PoiDetails, from?: ApiTrail): ApiTrail {
  const website = details.website.trim();
  const phone = details.phone.trim();
  return {
    ...from,
    guid: from?.guid ?? '',
    name: details.name.trim(),
    description: details.notes.trim(),
    routeType: POINT_OF_INTEREST,
    detail:
      website || phone ? JSON.stringify({ website: website || undefined, phone: phone || undefined }) : '',
    points: [{ ...poiPoint(position, details, from?.points?.[0]), website: undefined, phone: undefined }],
  };
}

/** A standalone POI's place and details. */
export function readPoi(json: ApiTrail): { position: LonLat; details: PoiDetails } | undefined {
  const point = json.points?.find((p) => !p.deleted);
  if (!point) return undefined;
  const details = poiDetailsOf(point, json.detail);
  return {
    position: [point.longitude, point.latitude],
    details: { ...details, name: details.name || json.name || '' },
  };
}

/** XpeditionGO's POI icons, chosen from what kind of place it is (OpenStreetMap's value). */
export function poiIconFor(kind: string | undefined): PoiIcon {
  if (!kind) return 'pin';
  if (/^(peak|volcano|hill)$/.test(kind)) return 'summit';
  if (/^(viewpoint)$/.test(kind)) return 'viewpoint';
  if (/^(cafe)$/.test(kind)) return 'cafe';
  if (/^(restaurant|fast_food|pub|bar|food_court)$/.test(kind)) return 'food';
  if (/^(camp_site|caravan_site)$/.test(kind)) return 'camp';
  if (/^(wilderness_hut|alpine_hut|shelter)$/.test(kind)) return 'shelter';
  if (/^(drinking_water|water_point|spring)$/.test(kind)) return 'water';
  if (/^(toilets)$/.test(kind)) return 'toilet';
  if (/^(parking)$/.test(kind)) return 'parking';
  if (/^(bridge)$/.test(kind)) return 'bridge';
  if (/^(gate)$/.test(kind)) return 'gate';
  if (/^(information)$/.test(kind)) return 'info';
  return 'pin';
}

/**
 * A long web address as it is shown: the site and the start of the path ("google.com/maps/@53.37…"); the link
 * itself keeps the whole address.
 */
export function shortWebsite(href: string, max = 40): string {
  try {
    const url = new URL(href);
    const text =
      url.host.replace(/^www\./, '') + (url.pathname === '/' ? '' : decodeURIComponent(url.pathname));
    return text.length > max ? `${text.slice(0, max - 1)}…` : text;
  } catch {
    return href.length > max ? `${href.slice(0, max - 1)}…` : href;
  }
}

/** A web address as a link: "example.com" becomes "https://example.com". Not a link if it isn't web. */
export function websiteHref(website: string): string | undefined {
  const value = website.trim();
  if (!value) return undefined;
  const href = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  try {
    const url = new URL(href);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : undefined;
  } catch {
    return undefined;
  }
}
